import { useEffect, useRef } from "react";
import { Platform } from "react-native";
import { usePathname } from "expo-router";
import type {
  PageContextEntity,
  PageContextEnvelope,
  PageContextScreen,
} from "@tabbio/types";
import { isChatRoutePath, normalizeRoutePath } from "./chat-navigation";

/**
 * Which screen the content panel is currently showing.
 *
 * Phase 1 derived this from the pathname alone, which is enough to pick the
 * right contextual intro for the agent chat column. Phase 2 promoted the type
 * to `@tabbio/types` (this re-export keeps Phase 1 imports working) and added
 * a publish/subscribe store so routes can attach a full `PageContextEnvelope`
 * (screen + entity id + screen state) that rides along with the user's
 * message so the agent can resolve "this job" / "this CV".
 */
export type { PageContextScreen };

/**
 * Maps a router pathname to a screen key.
 *
 * Order matters: the more specific prefixes are tested first so `/cv/tailored/x`
 * does not resolve to the base CV screen. `normalizeRoutePath` strips expo-router
 * group segments (`/(app)`), the query string and any trailing slash first.
 */
export function derivePageContextScreen(
  pathname: string | null | undefined,
): PageContextScreen {
  const path = normalizeRoutePath(pathname);

  if (path === "/chat" || path.startsWith("/chat/")) return "chat";

  // My Tabbio replaced the tailored-CV list. It is a collection, with no
  // selected document entity; the broad /cv rule below describes an editor.
  if (path === "/cv/my-tabbio" || path.startsWith("/cv/tailored")) return "cv-tailored";
  if (path === "/cv" || path.startsWith("/cv/")) return "cv";

  // `/jobs/<id>` is a single posting; `/jobs` (and its sub-tabs) is the feed.
  if (path.startsWith("/jobs/")) {
    const rest = path.slice("/jobs/".length);
    // Guard against future non-id sub-routes such as `/jobs/saved`.
    return rest && !rest.includes("/") ? "job-detail" : "jobs";
  }
  if (path === "/jobs") return "jobs";

  // Employer Phase 1 surfaces.
  if (path.startsWith("/applications/")) return "application-detail";
  if (path === "/applications") return "applications";
  if (path === "/company" || path.startsWith("/company/")) {
    return "company-page";
  }

  if (path === "/skills" || path.startsWith("/skills/")) return "skills";
  if (path === "/auto-apply" || path.startsWith("/auto-apply/")) {
    return "automation";
  }
  if (path === "/connectors" || path.startsWith("/connectors/")) {
    return "connectors";
  }
  if (path.startsWith("/settings/memory")) return "memory";
  if (path.startsWith("/settings/mcp")) return "connectors";
  if (path.startsWith("/settings/notifications")) return "notifications";
  if (path === "/messages" || path.startsWith("/messages/")) return "messages";
  if (path === "/notifications" || path.startsWith("/notifications/")) {
    return "notifications";
  }
  if (path === "/analytics" || path.startsWith("/analytics/")) {
    return "analytics";
  }
  if (path === "/profile" || path.startsWith("/profile/")) return "profile";
  if (path === "/services" || path.startsWith("/services/")) {
    return "application-studio";
  }
  if (path === "/settings" || path.startsWith("/settings/")) return "settings";

  return "other";
}

// ─── Published page-context store (web) ─────────────────────────────────────
//
// Module-level pub/sub, same shape as `components/tour/tour-actions.ts`.
// Screens publish an envelope for THEIR route; the chat transport reads the
// envelope for the LIVE pathname at send time. Keying entries by route means a
// stale entity id from a previous screen can never leak onto the next one:
// a route that never published simply falls back to `derivePageContextScreen`.

type PageContextListener = () => void;

const publishedByRoute = new Map<string, PageContextEnvelope>();
const listeners = new Set<PageContextListener>();

function notifyPageContextListeners() {
  listeners.forEach((listener) => listener());
}

export function subscribePageContext(listener: PageContextListener) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Publishes (or replaces) the envelope for its own normalized route. */
export function publishPageContext(envelope: PageContextEnvelope) {
  const route = normalizeRoutePath(envelope.route);
  publishedByRoute.set(route, { ...envelope, route });
  notifyPageContextListeners();
}

/** Removes the published envelope for a route (screen unmounted). */
export function unpublishPageContext(route: string | null | undefined) {
  if (publishedByRoute.delete(normalizeRoutePath(route))) {
    notifyPageContextListeners();
  }
}

/**
 * The envelope describing what the content panel shows for `pathname` NOW.
 *
 * - Chat routes return `null`: the content panel is hidden there (chat-only
 *   layout), so there is nothing for the agent to resolve "this" against.
 * - A route with a published envelope returns it verbatim.
 * - Any other route falls back to a minimal derived envelope (screen from the
 *   pathname, no entity) so zero-adoption routes still report where the user is.
 */
export function getPageContext(
  pathname: string | null | undefined,
): PageContextEnvelope | null {
  const route = normalizeRoutePath(pathname);
  if (isChatRoutePath(route)) return null;

  const published = publishedByRoute.get(route);
  if (published) return published;

  return {
    v: 1,
    screen: derivePageContextScreen(route),
    route,
    capturedAt: new Date().toISOString(),
  };
}

/** Test-only helper: clears every published envelope. */
export function resetPageContextForTesting() {
  publishedByRoute.clear();
  listeners.clear();
}

// ─── Publisher hook ─────────────────────────────────────────────────────────

export type PublishPageContextInput = {
  screen: PageContextScreen;
  entity?: PageContextEntity;
  state?: Record<string, unknown>;
};

// Mirror the server-side caps in @tabbio/utils so a client envelope is never
// silently dropped by validation. An over-long id cannot be truncated without
// corrupting it, so the entity is omitted instead.
const ROUTE_MAX = 256;
const ENTITY_ID_MAX = 128;
const ENTITY_LABEL_MAX = 200;
const STATE_MAX_CHARS = 2048;

function sanitizeEntity(
  entity: PageContextEntity | undefined,
): PageContextEntity | undefined {
  if (!entity || !entity.id || entity.id.length > ENTITY_ID_MAX) {
    return undefined;
  }
  const label = entity.label?.trim();
  return {
    type: entity.type,
    id: entity.id,
    ...(label ? { label: label.slice(0, ENTITY_LABEL_MAX) } : {}),
  };
}

function sanitizeState(
  state: Record<string, unknown> | undefined,
): Record<string, unknown> | undefined {
  if (!state || Object.keys(state).length === 0) return undefined;
  try {
    const serialized = JSON.stringify(state) ?? "";
    // Better to arrive without state than have the API drop the envelope.
    return serialized.length > STATE_MAX_CHARS ? undefined : state;
  } catch {
    return undefined;
  }
}

/**
 * Publishes this screen's page context while the component is mounted, and
 * removes it on unmount. Web-only by design: native chat is a route of its
 * own, so on iOS/Android this is a no-op. Safe to call from shared files.
 *
 * The screen's OWN route is latched on first render. `usePathname()` is
 * global, and expo-router keeps background screens mounted, so a stack-parent
 * screen re-renders with the foreground screen's pathname; publishing under
 * the live pathname would let the jobs feed clobber `/jobs/<id>`'s envelope.
 * With the latch, a screen can only ever write its own route key, and while
 * it is in the background (pathname diverged) its entry is withdrawn.
 *
 * Pass `null` while the screen has nothing to report yet (e.g. entity still
 * loading); the route then falls back to the derived screen key.
 */
export function usePublishPageContext(input: PublishPageContextInput | null) {
  const pathname = usePathname();

  const ownRouteRef = useRef<string | null>(null);
  if (ownRouteRef.current === null) {
    ownRouteRef.current = normalizeRoutePath(pathname);
  }

  // The input object is rebuilt every render; serialize it for the effect
  // dependency and read the latest value through a ref inside the effect.
  const inputRef = useRef(input);
  inputRef.current = input;
  const serializedInput = input ? JSON.stringify(input) : null;

  useEffect(() => {
    if (Platform.OS !== "web") return;

    const ownRoute = ownRouteRef.current;
    if (!ownRoute || ownRoute.length > ROUTE_MAX) return;

    const liveRoute = normalizeRoutePath(pathname);
    const current = inputRef.current;

    if (liveRoute !== ownRoute || !current) {
      // Screen is in the background (another route is on display) or has
      // nothing to report yet: withdraw any earlier publish so it cannot go
      // stale. It republishes when focus returns / data arrives.
      unpublishPageContext(ownRoute);
      return;
    }

    publishPageContext({
      v: 1,
      screen: current.screen,
      route: ownRoute,
      entity: sanitizeEntity(current.entity),
      state: sanitizeState(current.state),
      capturedAt: new Date().toISOString(),
    });

    return () => {
      unpublishPageContext(ownRoute);
    };
  }, [pathname, serializedInput]);
}
